import { useCallback, useEffect, useState } from "react";

import { apiClient } from "../api/client.js";
import { useAuth } from "../auth/AuthContext.js";

interface Fleet {
  id: string;
  name: string;
  slug: string;
  status: string;
  created_at: string;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function statusBadge(status: string) {
  switch (status) {
    case "active":
      return (
        <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-green-500/20 text-green-400">
          Active
        </span>
      );
    case "inactive":
      return (
        <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-gray-500/20 text-gray-400">
          Inactive
        </span>
      );
    default:
      return (
        <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-gray-500/20 text-gray-400">
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </span>
      );
  }
}

export function Fleets() {
  const { user } = useAuth();
  const [fleets, setFleets] = useState<Fleet[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formName, setFormName] = useState("");
  const [formSlug, setFormSlug] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const fetchFleets = useCallback(async () => {
    try {
      const res = await apiClient["/api/v1/admin/fleets"]["get"]({
        parameters: {},
        body: null,
      });
      if (res.code === 200) {
        setFleets(res.body as Fleet[]);
      }
    } catch {
      // Ignore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFleets();
  }, [fetchFleets]);

  const handleCreate = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!formName.trim() || !formSlug.trim()) return;

      setSubmitting(true);
      setFormError(null);

      try {
        const res = await apiClient["/api/v1/admin/fleets"]["post"]({
          parameters: {},
          body: {
            name: formName.trim(),
            slug: formSlug.trim(),
          },
        });
        if (res.code === 201) {
          setShowForm(false);
          setFormName("");
          setFormSlug("");
          fetchFleets();
        } else {
          const body = res.body as { message?: string };
          setFormError(body.message ?? "Failed to create fleet");
        }
      } catch {
        setFormError("Network error");
      } finally {
        setSubmitting(false);
      }
    },
    [formName, formSlug, fetchFleets],
  );

  return (
    <main className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-text-primary">Fleets</h1>
        {user?.is_platform_admin && (
          <button
            onClick={() => setShowForm(!showForm)}
            className="px-4 py-2 text-sm rounded-md bg-accent text-white hover:bg-accent-hover transition-colors"
          >
            {showForm ? "Cancel" : "Create Fleet"}
          </button>
        )}
      </div>

      {showForm && user?.is_platform_admin && (
        <form
          onSubmit={handleCreate}
          className="bg-surface-raised border border-border rounded-lg p-4 mb-6 space-y-3"
        >
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-text-secondary mb-1">Name *</label>
              <input
                type="text"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="Northern Distribution"
                required
                className="w-full px-3 py-2 rounded-md bg-surface border border-border text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent transition-colors"
              />
            </div>
            <div>
              <label className="block text-sm text-text-secondary mb-1">Slug *</label>
              <input
                type="text"
                value={formSlug}
                onChange={(e) => setFormSlug(e.target.value)}
                placeholder="northern-distribution"
                required
                className="w-full px-3 py-2 rounded-md bg-surface border border-border text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent transition-colors"
              />
            </div>
          </div>
          {formError && <p className="text-sm text-red-400">{formError}</p>}
          <button
            type="submit"
            disabled={submitting || !formName.trim() || !formSlug.trim()}
            className="px-4 py-2 text-sm rounded-md bg-accent text-white hover:bg-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? "Creating..." : "Create"}
          </button>
        </form>
      )}

      {loading && <p className="text-text-muted text-sm">Loading fleets...</p>}

      {!loading && fleets.length === 0 && (
        <div className="bg-surface-raised border border-border rounded-lg p-6 text-center">
          <p className="text-text-secondary">No fleets yet.</p>
          <p className="text-text-muted text-sm mt-1">
            {user?.is_platform_admin
              ? "Create your first fleet to get started."
              : "You are not a member of any fleets."}
          </p>
        </div>
      )}

      {!loading && fleets.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-border">
                <th className="pb-2 text-sm font-medium text-text-muted">Name</th>
                <th className="pb-2 text-sm font-medium text-text-muted">Slug</th>
                <th className="pb-2 text-sm font-medium text-text-muted">Status</th>
                <th className="pb-2 text-sm font-medium text-text-muted">Created</th>
              </tr>
            </thead>
            <tbody>
              {fleets.map((f) => (
                <tr key={f.id} className="border-b border-border/50 hover:bg-surface-raised/50">
                  <td className="py-3">
                    <a
                      href={`/fleets/${f.id}`}
                      className="text-text-primary hover:text-accent transition-colors no-underline font-medium"
                    >
                      {f.name}
                    </a>
                  </td>
                  <td className="py-3">
                    <span className="text-sm text-text-secondary font-mono">{f.slug}</span>
                  </td>
                  <td className="py-3">{statusBadge(f.status)}</td>
                  <td className="py-3">
                    <span className="text-sm text-text-muted">{formatDate(f.created_at)}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </main>
  );
}
